import React, { useEffect, useState, useMemo } from "react";
import { Row, Col } from "antd";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import styled from "styled-components";
import { theme } from "../../../styles";
import {
  Grid,
  BreadCrumbBox,
  Section,
  Typography,
  Card,
  Loading,
} from "../../../core";
import General from "./General";
import StockDetails from "./StockDetails";
import SizingProgram from "../../../components/SizingProgramTabel";
import PaymentHistory from "../../../components/SizerPaymentHistoryTabel";
import SizerRates from "../../../components/SizerRates";
import { useSizerData, parseSizerRates } from "../../../hooks/useSizerData";
import { getProgramsBySizer, getSizerBills, mapSizerBillsForPaymentHistory } from "../../../helper/utils/api/sizer";
import { getCompanyId } from "../../../helper/utils/companyId";

const { textColor } = theme;

const StyledLink = styled(Link)`
  font-weight: 500;
  font-size: 18px;
  line-height: 27px;
  display: table;
  margin: 10px auto;
  color: #ff5687 !important;
`;

const SizerInner = () => {
  useSizerData();
  let { id } = useParams();
  const sizerList = useSelector((state) => state.sizer.sizer) || [];
  const [programs, setPrograms] = useState([]);
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const SizerData = sizerList.find(
    (sizer) => String(sizer.sizer_id) === String(id)
  );

  useEffect(() => {
    const load = async () => {
      const companyId = getCompanyId();
      if (!id || !companyId) return;
      setIsLoading(true);
      try {
        const [progRes, billRes] = await Promise.all([
          getProgramsBySizer(id, companyId),
          getSizerBills(companyId, id),
        ]);
        setPrograms(Array.isArray(progRes.data) ? progRes.data : []);
        const bills = Array.isArray(billRes.data)
          ? billRes.data
          : billRes.data?.result || [];
        setPayments(mapSizerBillsForPaymentHistory(bills).slice(0, 5));
      } catch (error) {
        console.log("sizer programs error", error);
      }
      setIsLoading(false);
    };
    load();
  }, [id]);

  const sizerRate = useMemo(
    () => parseSizerRates(SizerData).slice(0, 5),
    [SizerData]
  );

  const yarnInStockitems = useMemo(
    () =>
      parseSizerRates(SizerData)
        .slice(0, 8)
        .map((rate, i) => ({
          key: i,
          tittle: rate.quality || rate.yarn_quality || `Quality ${i + 1}`,
          value: rate.rate || rate.value || "-",
        })),
    [SizerData]
  );

  if (!SizerData) {
    return <Loading />;
  }

  return (
    <div className="mb-50">
      {isLoading ? <Loading /> : null}
      <Grid>
        <BreadCrumbBox tittle="Sizer Inner" />
        <Row type="flex" justify="space-between" gutter={[16, 16]}>
          <Col lg={12} xs={24}>
            <Section padding="20px" minheight="595px">
              <General SizerData={SizerData} />
            </Section>
          </Col>
          <Col lg={11} xs={24}>
            <Section padding="20px" minheight="595px">
              <StockDetails />
            </Section>
          </Col>
        </Row>
        <Section padding="20px">
          <Typography
            as="h3"
            weight="bold"
            letterSpacing="0.2px"
            color={textColor.primary}
            text="Sizing Program"
          />
          <SizingProgram
            SizingProgramData={programs.slice(0, 5)}
            pagination={false}
          />
          <StyledLink to="/sizing-program">View All</StyledLink>
        </Section>
        <Row type="flex" justify="space-between" gutter={[16, 16]}>
          <Col lg={12} xs={24}>
            <Section padding="20px" className="mt-20">
              <Typography as="h3" color="#233B67" align="center" weight="bold">
                Payment History
              </Typography>
              <PaymentHistory PaymentHistoryData={payments} pagination={false} />
              <StyledLink to="/sizer-payment-history">View All</StyledLink>
            </Section>
          </Col>
          <Col lg={12} xs={24}>
            <Section padding="20px" className="mt-20">
              <Typography as="h3" color="#233B67" align="center" weight="bold">
                Sizer Rates
              </Typography>
              <SizerRates pagination={false} sizerRates={sizerRate} />
              <StyledLink to={`/sizer/${id}/sizer-rates`}>View All</StyledLink>
            </Section>
          </Col>
        </Row>
        <Section padding="20px">
          <Typography as="h3" color="#233B67" align="center" weight="bold">
            Yarn in Stock
          </Typography>
          <Row type="flex" justify="center" gutter={[16, 16]}>
            {yarnInStockitems.map((items) => (
              <Col xl={6} sm={12} key={items.key}>
                <Card tittle={items.tittle} value={items.value} />
              </Col>
            ))}
          </Row>
          <StyledLink to="/yarn-stock">View All</StyledLink>
        </Section>
      </Grid>
    </div>
  );
};

export default SizerInner;
